import React, { useState, useEffect, useMemo, useRef } from "react";
import styled from "styled-components";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { useToasts } from "react-toast-notifications";
import {
  LOAD_EMPLOYEES,
  SG_GET_EMPLOYEE_ORG_CATEGORIES,
  SG_GET_SURVEY_STRUCTURES,
  SG_REGISTER_EMPLOYEE_CULTURE,
  UPDATE_EMPLOYEE,
  SG_UPDATE_EMPLOYEE_AUDIT,
  SG_ADD_ORGANIZATION_GROUP,
  SG_BULK_UPDATE_ORGANIZATION_GROUP,
  SG_DELETE_ORGANIZATION_GROUP,
  SG_GET_EMPLOYEE_PERMISSIONS,
  SG_EDIT_EMPLOYEE_PERMISSION,
  SG_ADD_EMPLOYEE_PERMISSION,
} from "../../constants/actions";
import {
  ADD_ORGANIZATION_GROUP,
  GET_ORGANIZATION_GROUP as API_GET_ORGANIZATION_GROUP,
  ADD_ORGANIZATION_GROUP_MEMBERS_BY_CATEGORY,
  EDIT_ORGANIZATION_GROUP,
  BULK_UPDATE_ORGANIZATION_GROUP as API_BULK_UPDATE_ORGANIZATION_GROUP,
  GET_ORGANIZATION_GROUP_MEMBERS as API_GET_ORGANIZATION_GROUP_MEMBERS,
  GET_EMPLOYEE_PERMISSIONS as API_GET_EMPLOYEE_PERMISSIONS,
  EDIT_EMPLOYEE as API_EDIT_EMPLOYEE,
  EDIT_EMPLOYEE_AUDIT as API_EDIT_EMPLOYEE_AUDIT,
  ADD_EMPLOYEE_PERMISSION as API_ADD_EMPLOYEE_PERMISSION,
  EDIT_EMPLOYEE_PERMISSION as API_EDIT_EMPLOYEE_PERMISSION,
} from "../../constants/api";
import {
  ANALYTICS_CARD_PERMISSIONS,
  analyticsPermissionsToDetails,
  createAnalyticsPermissionState,
  isAnalyticsAuthDetail,
  parseAnalyticsPermissionsFromDetails,
} from "../../utilities/analyticsPermissions";
import AccessBreadcrumb from "./AccessBreadcrumb";
import ManagerGroupParticipationScanner from "./ManagerGroupParticipationScanner";

// Small helper delay
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const STEP1_THROTTLE_MS = 1500;
const BULK_ADD_THROTTLE_MS = 1500;
const PREVIEW_ANCHOR_THROTTLE_MS = 250;
const PERMISSION_BULK_THROTTLE_MS = 1000;
const PERMISSION_GROUP_LIST_PAGE_SIZE = 5000;
const BULK_REMOVE_CHUNK_SIZE = 500;
const PERMISSION_GROUP_LIST_PAGE_THROTTLE_MS = 200;
const GROUP_MEMBERS_PAGE_THROTTLE_MS = 200;
const DUPLICATE_COUNT_THROTTLE_MS = 350;

// CSV line parse (basic quotes handling for commas inside quotes)
function parseCsvLine(line) {
  const result = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }
  result.push(current.trim());
  return result;
}

// Split anchors in a field into array using common delimiters (comma, ;, or |)
function splitAnchors(fieldValue) {
  if (!fieldValue) return [];
  return fieldValue
    .split(/[|;,\n\r]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

const BulkImportManagers = ({ onBack }) => {
  const dispatch = useDispatch();
  const { addToast } = useToasts();

  // Redux state
  const { employees, get_auth, employeeCategories, surveyStructure, organization_groups, employeePermission } = useSelector(
    (state) => ({
      employees: Array.isArray(state.employees?.employees) ? state.employees.employees : [],
      get_auth: state.auth,
      employeeCategories: Array.isArray(state.employee_category?.employee_org_category)
        ? state.employee_category.employee_org_category
        : [],
      surveyStructure: state.surveystructure?.survey_structure || [],
      organization_groups: Array.isArray(state.organization_group?.organization_groups)
        ? state.organization_group.organization_groups
        : [],
      employeePermission: state.employee_permission,
    }),
    shallowEqual
  );

  // UI state
  const [showScanner, setShowScanner] = useState(false);
  const [step, setStep] = useState(1); // 1 = parse/import, 2 = sync table
  const [rawText, setRawText] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [processProgress, setProcessProgress] = useState({
    current: 0,
    total: 0,
    currentEmail: "",
  });

  // Parsed rows:
  // { email, groupName, anchors: string[], employeeId?, isManager?, groupId?, existedBefore?, existingMembersCount? }
  const [rows, setRows] = useState([]);

  // Cache of manager groups keyed by managerId: { groupId, membersCount }
  const [managerGroups, setManagerGroups] = useState({});
  // Snapshot map built from org-wide permission group list: managerId -> { groupId, membersCount, name }
  const permissionGroupsByManagerIdRef = useRef(new Map());
  const [rowActionState, setRowActionState] = useState({}); // { [employeeId]: { adding?: boolean, clearing?: boolean, lastAction?: 'added'|'cleared' } }
  const [bulkState, setBulkState] = useState({ adding: false, clearing: false });
  const [permissionState, setPermissionState] = useState({}); // { [employeeId]: { hasLogin?, noraAI?, readFeedback?, accessAnalytics?, dirty?: boolean } }
  const [permissionBulkState, setPermissionBulkState] = useState({
    saving: false,
    current: 0,
    total: 0,
    currentEmail: "",
  });
  const [expandedRows, setExpandedRows] = useState({}); // { [employeeId]: bool }
  const [bulkPreset, setBulkPreset] = useState({
    hasLogin: false,
    noraAI: false,
    readFeedback: false,
    accessAnalytics: false,
    pdfReports: false,
    specialPermission: false,
    analyticsPermissions: createAnalyticsPermissionState(false),
  });
  const [rowCategoryMatches, setRowCategoryMatches] = useState({}); // { [employeeId]: { loading: bool, count: number, rawMatches: Set, filters: array } }
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    type: null,
    manager: null,
    message: '',
    onConfirm: null,
    onCancel: null,
  });
  const [duplicateAudit, setDuplicateAudit] = useState({
    scanning: false,
    error: null,
    duplicates: [],
    lastScannedAt: null,
  });
  const [dupCounts, setDupCounts] = useState({}); // { [groupId]: { loading, count, error } }
  const [dupCountProgress, setDupCountProgress] = useState({
    running: false,
    current: 0,
    total: 0,
    currentGroupId: null,
  });
  const dupRunIdRef = useRef(0);
  const [previewProgress, setPreviewProgress] = useState({
    running: false,
    currentManager: 0,
    totalManagers: 0,
    currentEmail: "",
    anchorsCompleted: 0,
    anchorsTotal: 0,
  });
  const [bulkAddProgress, setBulkAddProgress] = useState({
    running: false,
    current: 0,
    total: 0,
    currentEmail: "",
  });

  // Keep a live ref of employees for async flows
  const employeesRef = useRef(employees);
  useEffect(() => { employeesRef.current = employees; }, [employees]);
  const previewRunIdRef = useRef(0);

  // Load detailed employee permissions once when on step 2 so we can map Nora AI flags
  useEffect(() => {
    if (step === 2 && get_auth?.organization_id) {
      dispatch({
        type: SG_GET_EMPLOYEE_PERMISSIONS,
        payload: `organization=${get_auth.organization_id}`,
      });
    }
  }, [dispatch, step, get_auth?.organization_id]);

  // Load references when page loads
  useEffect(() => {
    if (!employees?.length) dispatch({ type: LOAD_EMPLOYEES });
    if (get_auth?.organization_id) {
      dispatch({ type: SG_GET_EMPLOYEE_ORG_CATEGORIES, payload: `organization=${get_auth.organization_id}` });
      dispatch({ type: SG_GET_SURVEY_STRUCTURES, payload: `organization=${get_auth.organization_id}` });
    }
  }, [employees?.length, dispatch, get_auth?.organization_id]);

  // Map normalized anchor/tag name -> category/value metadata for API filters
  const anchorToCategoryMap = useMemo(() => {
    const map = new Map();
    const struct = Array.isArray(surveyStructure) ? surveyStructure[0] : null;
    const cats = struct?.categories || [];
    cats.forEach((cat) => {
      const priority = cat.priority || null;
      const category_name_id = cat.id;
      const category_name = cat.name;
      (cat.options || []).forEach((opt) => {
        const key = String(opt.name || opt.text || "").toLowerCase();
        if (!key) return;
        map.set(key, {
          category_name_id,
          category_name,
          category_value_id: opt.id,
          category_value: opt.name || opt.text || "",
          priority,
        });
      });
    });
    return map;
  }, [surveyStructure]);

  // Refresh category-based match previews when rows are ready
  useEffect(() => {
    if (step !== 2) return;
    const runId = (previewRunIdRef.current += 1);
    const eligibleRows = (rows || []).filter((r) => r?.employeeId);

    setPreviewProgress({
      running: eligibleRows.length > 0,
      currentManager: 0,
      totalManagers: eligibleRows.length,
      currentEmail: "",
      anchorsCompleted: 0,
      anchorsTotal: 0,
    });

    (async () => {
      try {
        for (let i = 0; i < eligibleRows.length; i++) {
          const row = eligibleRows[i];
          if (previewRunIdRef.current !== runId) return;

          setPreviewProgress((prev) => ({
            ...prev,
            running: true,
            currentManager: i + 1,
            totalManagers: eligibleRows.length,
            currentEmail: row?.email || "",
            anchorsCompleted: 0,
            anchorsTotal: (row?.anchors || []).length,
          }));

          // eslint-disable-next-line no-await-in-loop
          await previewMatchesForRow(row, {
            onProgress: ({ completed, total }) => {
              if (previewRunIdRef.current !== runId) return;
              setPreviewProgress((prev) => ({
                ...prev,
                anchorsCompleted: completed,
                anchorsTotal: total,
              }));
            },
          });
        }
      } finally {
        if (previewRunIdRef.current !== runId) return;
        setPreviewProgress({
          running: false,
          currentManager: 0,
          totalManagers: 0,
          currentEmail: "",
          anchorsCompleted: 0,
          anchorsTotal: 0,
        });
      }
    })();

    return () => {
      // invalidate this run
      previewRunIdRef.current += 1;
    };
  }, [rows, step, anchorToCategoryMap]);

  // Prefetch current permission-group members for existing managers so "Clear X contacts" is accurate.
  // NOTE: We intentionally do NOT prefetch organization-group detail (/organization-group/:id/)
  // to derive member counts. Counts are sourced from the org-wide list (`members_count`)
  // to avoid duplicate slow calls and to guarantee every row has a count.

  const parseInputToArray = () => {
    const lines = rawText
      .split(/\n+/)
      .map((l) => l.trim())
      .filter((l) => l && !l.startsWith('#'));
    const parsed = lines.map((line) => {
      const cols = parseCsvLine(line);
      const email = cols[0] || '';
      const groupName = cols[1] || '';
      let anchorsField = '';
      if (cols.length > 2) {
        anchorsField = cols.slice(2).join(',');
      }
      const anchors = splitAnchors(anchorsField);
      return { email: email.trim(), groupName: groupName.trim(), anchors };
    });
    // Filter invalid emails
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const valid = parsed.filter((r) => emailRegex.test(r.email));
    if (valid.length !== parsed.length) {
      addToast(`Skipped ${parsed.length - valid.length} invalid email row(s)`, { appearance: "warning", autoDismiss: true });
    }
    return valid;
  };

  const buildPermissionGroupMapFromList = (list) => {
    // managerId -> { groupId, membersCount, name }
    const map = new Map();
    (list || []).forEach((g) => {
      if (!g) return;
      const managerId = typeof g.manager === "object" ? g.manager?.id : g.manager;
      if (!managerId) return;
      const groupId = g.id;
      if (!groupId) return;
      const membersCount =
        typeof g.members_count === "number"
          ? g.members_count
          : Array.isArray(g.members)
          ? g.members.length
          : 0;
      if (!map.has(Number(managerId))) {
        map.set(Number(managerId), { groupId, membersCount, name: g.name });
      }
    });
    return map;
  };

  const fetchAllPermissionGroupsForOrg = async (orgId) => {
    if (!orgId) return [];
    const tryFetchAll = async (pageSize) => {
      const all = [];
      let page = 1;
      // DRF pagination: stop when next is null/empty; also keep a hard safety cap
      for (let i = 0; i < 200; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        const res = await API_GET_ORGANIZATION_GROUP({
          organization: orgId,
          group_type: "permission",
          page,
          page_size: pageSize,
        });
        const data = res?.data || {};
        const results = Array.isArray(data?.results)
          ? data.results
          : Array.isArray(data)
          ? data
          : [];
        all.push(...results);
        if (!data?.next) break;
        page += 1;
        // eslint-disable-next-line no-await-in-loop
        await delay(PERMISSION_GROUP_LIST_PAGE_THROTTLE_MS);
      }
      return all;
    };

    try {
      return await tryFetchAll(PERMISSION_GROUP_LIST_PAGE_SIZE);
    } catch (e) {
      // Defensive fallback: some environments reject large page_size values.
      try {
        return await tryFetchAll(500);
      } catch (_) {
        throw e;
      }
    }
  };

  // Targeted lookup to prevent duplicate permission groups when the org-wide list
  // is incomplete / throttled. This is intentionally small and safe to call.
  const lookupPermissionGroupByManager = async ({ orgId, managerId }) => {
    if (!orgId || !managerId) return null;
    try {
      const res = await API_GET_ORGANIZATION_GROUP({
        organization: orgId,
        group_type: "permission",
        manager: managerId,
        page: 1,
        page_size: 5,
      });
      const data = res?.data || {};
      const results = Array.isArray(data?.results)
        ? data.results
        : Array.isArray(data)
        ? data
        : [];
      const g = results?.[0];
      if (!g?.id) return null;
      const membersCount =
        typeof g.members_count === "number"
          ? g.members_count
          : Array.isArray(g.members)
          ? g.members.length
          : 0;
      return { groupId: g.id, membersCount, name: g.name };
    } catch (_) {
      return null;
    }
  };

  const ensureManager = async (row, existingPermissionGroupsByManagerId) => {
    const orgId = get_auth?.organization_id || get_auth?.selectedOrganization?.id;
    const buildManagerGroupName = (employee) => {
      const csvGroupName = (row?.groupName || "").trim();
      if (csvGroupName) return csvGroupName;

      let managerName = "";
      if (employee?.first_name || employee?.last_name) {
        managerName = `${employee.first_name || ""} ${employee.last_name || ""}`.trim();
      }
      if (!managerName) {
        managerName = employee?.email || "Unknown Manager";
      }
      return managerName;
    };
    // Try to find existing employee by email
    let emp = (employees || []).find((e) => (e.email || '').toLowerCase() === row.email.toLowerCase());
    if (emp) {
      const managerId = Number(emp.id);

      // Check manager state using org-wide permission group list map (fast, guarantees members_count),
      // and fall back to a targeted lookup by manager_id to prevent duplicates.
      const status =
        existingPermissionGroupsByManagerId?.get?.(managerId) ||
        permissionGroupsByManagerIdRef.current?.get?.(managerId) ||
        (await lookupPermissionGroupByManager({ orgId, managerId }));

      if (status && status.groupId) {
        const finalGroupName = buildManagerGroupName(emp);
        // Attempt to rename existing manager group to match CSV / default naming
        try {
          await EDIT_ORGANIZATION_GROUP({ id: status.groupId, name: finalGroupName });
        } catch (_) {
          // Non-fatal: keep going even if rename fails
        }
        // Keep our snapshot map warm for downstream logic
        try {
          if (permissionGroupsByManagerIdRef.current?.set) {
            permissionGroupsByManagerIdRef.current.set(managerId, {
              groupId: status.groupId,
              membersCount: status.membersCount || 0,
              name: status.name,
            });
          }
        } catch (_) {
          // ignore
        }
        setManagerGroups((prev) => ({
          ...prev,
          [emp.id]: { groupId: status.groupId, membersCount: status.membersCount || 0 },
        }));
        return {
          employeeId: emp.id,
          isManager: true,
          groupId: status.groupId,
          existedBefore: true,
          existingMembersCount: status.membersCount || 0,
        };
      }
      // Update audit minimal permissions for managers (do not change enable_login)
      dispatch({
        type: SG_UPDATE_EMPLOYEE_AUDIT,
        payload: {
          id: emp.id,
          access_data_analytics: 1,
          access_subfactor_data: 1,
          survey_status: 1,
          account_type: 9,
          first_name: emp.first_name,
          last_name: emp.last_name,
        },
      });
      // Create group via direct API for speed
      try {
        const managerName = buildManagerGroupName(emp);
        const payload = { name: managerName, description: "", organization: orgId, manager_id: emp.id, group_type: "permission" };
        const created = await ADD_ORGANIZATION_GROUP(payload);
        const groupId = created?.data?.id;
        setManagerGroups((prev) => ({ ...prev, [emp.id]: { groupId, membersCount: 0 } }));
        return { employeeId: emp.id, isManager: true, groupId, existedBefore: false, existingMembersCount: 0 };
      } catch (_) {
        // If create errored, re-check in case it actually succeeded but the response failed / timed out.
        const existingAfterError = await lookupPermissionGroupByManager({ orgId, managerId });
        if (existingAfterError?.groupId) {
          setManagerGroups((prev) => ({
            ...prev,
            [emp.id]: { groupId: existingAfterError.groupId, membersCount: existingAfterError.membersCount || 0 },
          }));
          try {
            if (permissionGroupsByManagerIdRef.current?.set) {
              permissionGroupsByManagerIdRef.current.set(managerId, {
                groupId: existingAfterError.groupId,
                membersCount: existingAfterError.membersCount || 0,
                name: existingAfterError.name,
              });
            }
          } catch (_) {
            // ignore
          }
          return {
            employeeId: emp.id,
            isManager: true,
            groupId: existingAfterError.groupId,
            existedBefore: true,
            existingMembersCount: existingAfterError.membersCount || 0,
          };
        }
        // Fall back to saga
        const managerName = buildManagerGroupName(emp);
        const payload = { name: managerName, description: "", organization: orgId, manager_id: emp.id, group_type: "permission" };
        dispatch({ type: SG_ADD_ORGANIZATION_GROUP, payload });
        // Wait briefly then re-check
        await delay(600);
        // Refresh permission-group list once and re-check map to avoid per-manager detail calls
        try {
          const allGroups = await fetchAllPermissionGroupsForOrg(orgId);
          const refreshedMap = buildPermissionGroupMapFromList(allGroups);
          permissionGroupsByManagerIdRef.current = refreshedMap;
          const status2 = refreshedMap.get(Number(emp.id));
          if (status2 && status2.groupId) {
            setManagerGroups((prev) => ({ ...prev, [emp.id]: { groupId: status2.groupId, membersCount: status2.membersCount || 0 } }));
            return { employeeId: emp.id, isManager: true, groupId: status2.groupId, existedBefore: false, existingMembersCount: status2.membersCount || 0 };
          }
        } catch (_) {
          // ignore
        }
        return { employeeId: emp.id, isManager: false, existedBefore: false };
      }
    } else {
      // Create employee via saga, then create group
      dispatch({
        type: SG_REGISTER_EMPLOYEE_CULTURE,
        payload: {
          email: row.email,
          first_name: '',
          last_name: '',
          organization: orgId,
          survey_status: 1,
          account_type: 9,
          read_feedback: 0,
          survey_add_users: 0,
          survey_schedule: 0,
        },
      });
      // Poll employees list to find new one
      let createdEmp = null;
      for (let i = 0; i < 10; i++) {
        await delay(400);
        createdEmp = (employeesRef.current || []).find((e) => (e.email || '').toLowerCase() === row.email.toLowerCase());
        if (createdEmp) break;
      }
      if (!createdEmp) {
        // Try one last refresh
        dispatch({ type: LOAD_EMPLOYEES });
        await delay(600);
        createdEmp = (employeesRef.current || []).find((e) => (e.email || '').toLowerCase() === row.email.toLowerCase());
      }
      if (!createdEmp) {
        addToast(`Could not create employee for ${row.email}`, { appearance: "error", autoDismiss: true });
        return { employeeId: null, isManager: false };
      }

      // Safety: if a permission group already exists for this manager_id, do NOT create another one.
      const existingForCreated = await lookupPermissionGroupByManager({
        orgId,
        managerId: Number(createdEmp.id),
      });
      if (existingForCreated?.groupId) {
        setManagerGroups((prev) => ({
          ...prev,
          [createdEmp.id]: { groupId: existingForCreated.groupId, membersCount: existingForCreated.membersCount || 0 },
        }));
        try {
          if (permissionGroupsByManagerIdRef.current?.set) {
            permissionGroupsByManagerIdRef.current.set(Number(createdEmp.id), {
              groupId: existingForCreated.groupId,
              membersCount: existingForCreated.membersCount || 0,
              name: existingForCreated.name,
            });
          }
        } catch (_) {
          // ignore
        }
        return {
          employeeId: createdEmp.id,
          isManager: true,
          groupId: existingForCreated.groupId,
          existedBefore: true,
          existingMembersCount: existingForCreated.membersCount || 0,
        };
      }

      // Create group via API
      try {
        const managerName = buildManagerGroupName(createdEmp);
        const payload = { name: managerName, description: "", organization: orgId, manager_id: createdEmp.id, group_type: "permission" };
        const created = await ADD_ORGANIZATION_GROUP(payload);
        const groupId = created?.data?.id;
        setManagerGroups((prev) => ({ ...prev, [createdEmp.id]: { groupId, membersCount: 0 } }));
        return { employeeId: createdEmp.id, isManager: true, groupId, existedBefore: false, existingMembersCount: 0 };
      } catch (_) {
        // If create errored, re-check in case it actually succeeded but the response failed / timed out.
        const existingAfterError = await lookupPermissionGroupByManager({
          orgId,
          managerId: Number(createdEmp.id),
        });
        if (existingAfterError?.groupId) {
          setManagerGroups((prev) => ({
            ...prev,
            [createdEmp.id]: { groupId: existingAfterError.groupId, membersCount: existingAfterError.membersCount || 0 },
          }));
          try {
            if (permissionGroupsByManagerIdRef.current?.set) {
              permissionGroupsByManagerIdRef.current.set(Number(createdEmp.id), {
                groupId: existingAfterError.groupId,
                membersCount: existingAfterError.membersCount || 0,
                name: existingAfterError.name,
              });
            }
          } catch (_) {
            // ignore
          }
          return {
            employeeId: createdEmp.id,
            isManager: true,
            groupId: existingAfterError.groupId,
            existedBefore: true,
            existingMembersCount: existingAfterError.membersCount || 0,
          };
        }
        return { employeeId: createdEmp.id, isManager: false, existedBefore: false };
      }
    }
  };

  const handleProcessAndContinue = async () => {
    if (isProcessing) return;
    setIsProcessing(true);
    const parsed = parseInputToArray();
    if (parsed.length === 0) {
      setIsProcessing(false);
      setProcessProgress({ current: 0, total: 0, currentEmail: "" });
      addToast("No valid rows to process", { appearance: "warning", autoDismiss: true });
      return;
    }
    // Ensure employees up to date
    if (!employees?.length) {
      dispatch({ type: LOAD_EMPLOYEES });
      await delay(500);
    }

    setProcessProgress({ current: 0, total: parsed.length, currentEmail: "" });

    try {
      // Fetch all permission groups once so counts are reliable and we avoid duplicate /:id/ calls
      const orgId = get_auth?.organization_id || get_auth?.selectedOrganization?.id;
      let permissionGroupMap = new Map();
      try {
        const allGroups = await fetchAllPermissionGroupsForOrg(orgId);
        permissionGroupMap = buildPermissionGroupMapFromList(allGroups);
        permissionGroupsByManagerIdRef.current = permissionGroupMap;
      } catch (_) {
        // Non-fatal: we can still create managers; counts may default to 0 for existing groups
        permissionGroupMap = new Map();
      }

      const updated = [];
      for (let i = 0; i < parsed.length; i++) {
        const r = parsed[i];
        setProcessProgress({ current: i + 1, total: parsed.length, currentEmail: r?.email || "" });
        // eslint-disable-next-line no-await-in-loop
        const res = await ensureManager(r, permissionGroupMap);
        updated.push({
          ...r,
          employeeId: res.employeeId || null,
          isManager: !!res.isManager,
          groupId: res.groupId || null,
          existedBefore: !!res.existedBefore,
          existingMembersCount: typeof res.existingMembersCount === "number" ? res.existingMembersCount : 0,
        });

        // Fixed throttle to avoid 429s on large imports
        if (i < parsed.length - 1) {
          // eslint-disable-next-line no-await-in-loop
          await delay(STEP1_THROTTLE_MS);
        }
      }
      setRows(updated);
      addToast("Manager check and creation complete", { appearance: "success", autoDismiss: true });
      setStep(2);
    } finally {
      setIsProcessing(false);
      setProcessProgress({ current: 0, total: 0, currentEmail: "" });
    }
  };

  // Helper to fetch and cache manager group meta (groupId + membersCount) WITHOUT detail calls.
  const getOrFetchManagerGroup = async (managerId) => {
    if (!managerId) return null;
    // Prefer the org-wide map we already built
    const fromMap = permissionGroupsByManagerIdRef.current?.get?.(Number(managerId));
    if (fromMap && fromMap.groupId) {
      const obj = { groupId: fromMap.groupId, membersCount: fromMap.membersCount || 0 };
      setManagerGroups((prev) => ({ ...prev, [managerId]: obj }));
      return obj;
    }

    // Prefer redux list (often contains members_count)
    const fromRedux = (organization_groups || []).find(g => {
      const mid = typeof g.manager === 'object' ? g.manager?.id : g.manager;
      return Number(mid) === Number(managerId);
    });
    if (fromRedux && fromRedux.id) {
      const membersCount =
        typeof fromRedux.members_count === "number"
          ? fromRedux.members_count
          : Array.isArray(fromRedux.members)
          ? fromRedux.members.length
          : 0;
      const obj = { groupId: fromRedux.id, membersCount };
      setManagerGroups((prev) => ({ ...prev, [managerId]: obj }));
      return obj;
    }

    const cached = managerGroups[managerId];
    if (cached && cached.groupId != null) return cached;
    const orgId = get_auth?.organization_id || get_auth?.selectedOrganization?.id;
    try {
      const res = await API_GET_ORGANIZATION_GROUP({ manager: managerId, organization: orgId, group_type: 'permission' });
      const list = res?.data?.results || res?.data || [];
      if (Array.isArray(list) && list[0]) {
        const group = list[0];
        const membersCount =
          typeof group?.members_count === "number"
            ? group.members_count
            : Array.isArray(group?.members)
            ? group.members.length
            : 0;
        const obj = { groupId: group.id, membersCount };
        setManagerGroups((prev) => ({ ...prev, [managerId]: obj }));
        return obj;
      }
    } catch (_) {
      // ignore
    }
    return null;
  };

  // Build filters for API calls based on anchors/tags in a row
  const buildCategoryFiltersForRow = (row) => {
    const filters = [];
    (row.anchors || []).forEach((name) => {
      const meta = anchorToCategoryMap.get(String(name || "").toLowerCase());
      if (meta) filters.push(meta);
    });
    return filters;
  };

  // Preview matching contacts for a manager using the add_members_by_category endpoint
  const previewMatchesForRow = async (row, options = {}) => {
    const { onProgress } = options;
    if (!row?.employeeId) return;
    const filtersList = buildCategoryFiltersForRow(row);
    if (!filtersList.length) {
      setRowCategoryMatches((prev) => ({
        ...prev,
        [row.employeeId]: {
          loading: false,
          count: 0,
          rawIds: [],
          filters: [],
          totalFilters: 0,
          completedFilters: 0,
        },
      }));
      return;
    }

    const totalFilters = filtersList.length;
    if (typeof onProgress === "function") {
      onProgress({ completed: 0, total: totalFilters });
    }

    setRowCategoryMatches((prev) => ({
      ...prev,
      [row.employeeId]: {
        ...(prev[row.employeeId] || {}),
        loading: true,
        count: 0,
        rawIds: [],
        filters: filtersList,
        totalFilters,
        completedFilters: 0,
      },
    }));

    const groupId = row.groupId;
    if (!groupId) {
      setRowCategoryMatches((prev) => ({
        ...prev,
        [row.employeeId]: {
          loading: false,
          count: 0,
          rawIds: [],
          filters: filtersList,
          error: "Group not found",
          totalFilters,
          completedFilters: 0,
        },
      }));
      return;
    }

    const uniqueIds = new Set();
    let aggregatedMatchCount = 0;
    let completedFilters = 0;

    for (const f of filtersList) {
      // Build a single-filter payload for this category
      const singleFilter = {
        category_name_id: f.category_name_id,
        category_value_id: f.category_value_id,
        priority: f.priority,
      };
      // Send name/value as fallback if IDs were missing for any reason
      if (f.category_name && !f.category_name_id) singleFilter.category_name = f.category_name;
      if (f.category_value && !f.category_value_id) singleFilter.category_value = f.category_value;

      try {
        const body = {
          category_filters: [singleFilter],
          preview: true,
        };

        const res = await ADD_ORGANIZATION_GROUP_MEMBERS_BY_CATEGORY({
          organization_group_id: groupId,
          body,
        });
        const data = res?.data || {};
        (data.employee_ids || []).forEach((id) => uniqueIds.add(id));
        aggregatedMatchCount += data.match_count || 0;
      } catch (error) {
        setRowCategoryMatches((prev) => ({
          ...prev,
          [row.employeeId]: {
            loading: false,
            count: 0,
            rawIds: [],
            filters: filtersList,
            error: error?.response?.data || true,
            totalFilters,
            completedFilters,
          },
        }));
        return;
      } finally {
        completedFilters += 1;
        if (typeof onProgress === "function") {
          onProgress({ completed: completedFilters, total: totalFilters });
        }
        const interimCount =
          uniqueIds.size > 0 ? uniqueIds.size : aggregatedMatchCount;

        setRowCategoryMatches((prev) => ({
          ...prev,
          [row.employeeId]: {
            ...(prev[row.employeeId] || {}),
            loading: completedFilters < totalFilters,
            count: interimCount,
            rawIds: Array.from(uniqueIds),
            filters: filtersList,
            totalFilters,
            completedFilters,
          },
        }));
        if (completedFilters < totalFilters) {
          // Throttle preview calls to reduce 429 risk on large tag sets
          // eslint-disable-next-line no-await-in-loop
          await delay(PREVIEW_ANCHOR_THROTTLE_MS);
        }
      }
    }

    const finalCount =
      uniqueIds.size > 0 ? uniqueIds.size : aggregatedMatchCount;

    setRowCategoryMatches((prev) => ({
      ...prev,
      [row.employeeId]: {
        ...(prev[row.employeeId] || {}),
        loading: false,
        count: finalCount,
        rawIds: Array.from(uniqueIds),
        filters: filtersList,
        totalFilters,
        completedFilters: totalFilters,
      },
    }));
  };

  const getMatchInfoForRow = (row) =>
    (row && row.employeeId && rowCategoryMatches[row.employeeId]) || {
      loading: false,
      count: 0,
      rawIds: [],
      filters: [],
       totalFilters: 0,
       completedFilters: 0,
    };

  // Determine which rows are eligible for bulk operations
  const getBulkAddCandidateRows = () =>
    rows.filter((r) => {
      if (!r.employeeId) return false;
      const s = rowActionState[r.employeeId] || {};
      if (s.lastAction === 'added') return false;
      const matchInfo = getMatchInfoForRow(r);
      return !matchInfo.loading && matchInfo.count > 0;
    });

  const getBulkClearCandidateRows = () =>
    rows.filter((r) => {
      if (!r.isManager || !r.existedBefore) return false;
      const s = rowActionState[r.employeeId] || {};
      if (s.lastAction === 'cleared') return false;
      return true;
    });

  // Core logic to add contacts for a single manager (no confirmation)
  const performAddContacts = async (row, options = {}) => {
    const { silent = false } = options;
    if (!row.employeeId) return { changed: false };

    const matchInfo = getMatchInfoForRow(row);
    const filtersList = buildCategoryFiltersForRow(row);
    if (!filtersList.length) {
      if (!silent) {
        addToast("No category tags available for this manager", { appearance: "info", autoDismiss: true });
      }
      return { changed: false };
    }
    if (matchInfo.loading) {
      if (!silent) {
        addToast("Still fetching matching contacts, try again shortly", { appearance: "info", autoDismiss: true });
      }
      return { changed: false };
    }
    if ((matchInfo.count || 0) === 0) {
      if (!silent) {
        addToast("No matching contacts found", { appearance: "info", autoDismiss: true });
      }
      return { changed: false };
    }

    setRowActionState((prev) => ({
      ...prev,
      [row.employeeId]: { ...(prev[row.employeeId] || {}), adding: true, lastAction: undefined },
    }));

    const groupId = row.groupId;
    if (!groupId) {
      if (!silent) {
        addToast("Manager group not found", { appearance: "error", autoDismiss: true });
      }
      setRowActionState((prev) => ({
        ...prev,
        [row.employeeId]: { ...(prev[row.employeeId] || {}), adding: false },
      }));
      return { changed: false };
    }

    const uniqueIds = Array.isArray(matchInfo.rawIds)
      ? Array.from(new Set(matchInfo.rawIds.filter((id) => id != null)))
      : [];

    if (!uniqueIds.length) {
      if (!silent) {
        addToast("No matching contacts found", { appearance: "info", autoDismiss: true });
      }
      setRowActionState((prev) => ({
        ...prev,
        [row.employeeId]: { ...(prev[row.employeeId] || {}), adding: false },
      }));
      return { changed: false };
    }

    try {
      dispatch({
        type: SG_BULK_UPDATE_ORGANIZATION_GROUP,
        payload: {
          organization_group_id: groupId,
          employee_ids: uniqueIds,
          action: "add",
          create_tokens: false,
          send_email: false,
        },
      });
    } catch (error) {
      if (!silent) {
        addToast("Failed to add contacts for this manager", { appearance: "error", autoDismiss: true });
      }
      setRowActionState((prev) => ({
        ...prev,
        [row.employeeId]: { ...(prev[row.employeeId] || {}), adding: false },
      }));
      return { changed: false };
    }

    if (!silent) {
      addToast(
        uniqueIds.length > 0
          ? `Adding ${uniqueIds.length} contact(s) to this manager's group...`
          : "No new contacts added (they may already be in the group)",
        { appearance: uniqueIds.length > 0 ? "success" : "info", autoDismiss: true }
      );
    }

    // Refresh cache lazily
    await delay(500);
    // Optimistically bump the stored count so every row stays consistent without hitting /:id/
    setRows((prev) =>
      (prev || []).map((r) =>
        r?.employeeId === row.employeeId
          ? {
              ...r,
              existingMembersCount:
                typeof r.existingMembersCount === "number"
                  ? r.existingMembersCount + uniqueIds.length
                  : uniqueIds.length,
            }
          : r
      )
    );
    setRowActionState((prev) => ({
      ...prev,
      [row.employeeId]: { ...(prev[row.employeeId] || {}), adding: false, lastAction: 'added' },
    }));

    return { changed: uniqueIds.length > 0, count: uniqueIds.length };
  };

  const chunk = (arr, size) => {
    const out = [];
    for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
    return out;
  };

  const fetchAllGroupMemberIds = async (groupId) => {
    const ids = [];
    let page = 1;
    for (let i = 0; i < 200; i += 1) {
      // eslint-disable-next-line no-await-in-loop
      const res = await API_GET_ORGANIZATION_GROUP_MEMBERS({
        group_id: groupId,
        params: { page, page_size: 500 },
      });
      const data = res?.data;
      const results = Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : [];
      results.forEach((m) => {
        const id =
          typeof m === "number"
            ? m
            : m?.employee_id || m?.id || (typeof m?.employee === "number" ? m.employee : m?.employee?.id);
        if (id != null) ids.push(id);
      });
      if (!data?.next) break;
      page += 1;
      // eslint-disable-next-line no-await-in-loop
      await delay(GROUP_MEMBERS_PAGE_THROTTLE_MS);
    }
    return Array.from(new Set(ids));
  };

  const countAllGroupMembers = async (groupId) => {
    const ids = new Set();
    let page = 1;
    for (let i = 0; i < 200; i += 1) {
      // eslint-disable-next-line no-await-in-loop
      const res = await API_GET_ORGANIZATION_GROUP_MEMBERS({
        group_id: groupId,
        params: { page, page_size: 500 },
      });
      const data = res?.data;
      const results = Array.isArray(data?.results)
        ? data.results
        : Array.isArray(data)
        ? data
        : [];
      results.forEach((m) => {
        const id =
          typeof m === "number"
            ? m
            : m?.employee_id ||
              m?.id ||
              (typeof m?.employee === "number" ? m.employee : m?.employee?.id);
        if (id != null) ids.add(id);
      });
      if (!data?.next) break;
      page += 1;
      // eslint-disable-next-line no-await-in-loop
      await delay(GROUP_MEMBERS_PAGE_THROTTLE_MS);
    }
    return ids.size;
  };

  const cancelDuplicateCountRun = () => {
    dupRunIdRef.current += 1;
    setDupCountProgress({ running: false, current: 0, total: 0, currentGroupId: null });
    setDupCounts((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((k) => {
        if (next[k]?.loading) next[k] = { ...next[k], loading: false };
      });
      return next;
    });
  };

  const fetchDuplicateGroupMemberCounts = async () => {
    if (dupCountProgress.running) return;
    const duplicates = duplicateAudit?.duplicates || [];
    const groupIds = [];
    duplicates.forEach((d) => {
      (d?.groups || []).forEach((g) => {
        if (g?.id != null) groupIds.push(g.id);
      });
    });
    const uniqueGroupIds = Array.from(new Set(groupIds)).filter(Boolean);
    if (!uniqueGroupIds.length) {
      addToast("No duplicate groups to count", { appearance: "info", autoDismiss: true });
      return;
    }

    const runId = (dupRunIdRef.current += 1);
    setDupCountProgress({
      running: true,
      current: 0,
      total: uniqueGroupIds.length,
      currentGroupId: null,
    });

    try {
      for (let i = 0; i < uniqueGroupIds.length; i += 1) {
        if (dupRunIdRef.current !== runId) return;
        const groupId = uniqueGroupIds[i];

        setDupCountProgress((prev) => ({
          ...prev,
          running: true,
          current: i,
          total: uniqueGroupIds.length,
          currentGroupId: groupId,
        }));

        setDupCounts((prev) => ({
          ...prev,
          [groupId]: { ...(prev[groupId] || {}), loading: true, error: null },
        }));

        try {
          // eslint-disable-next-line no-await-in-loop
          const count = await countAllGroupMembers(groupId);
          if (dupRunIdRef.current !== runId) return;
          setDupCounts((prev) => ({
            ...prev,
            [groupId]: { loading: false, count, error: null },
          }));
        } catch (e) {
          if (dupRunIdRef.current !== runId) return;
          setDupCounts((prev) => ({
            ...prev,
            [groupId]: {
              loading: false,
              count: null,
              error: e?.response?.data || e?.message || true,
            },
          }));
        }

        setDupCountProgress((prev) => ({
          ...prev,
          running: true,
          current: i + 1,
          total: uniqueGroupIds.length,
          currentGroupId: groupId,
        }));

        if (i < uniqueGroupIds.length - 1) {
          // eslint-disable-next-line no-await-in-loop
          await delay(DUPLICATE_COUNT_THROTTLE_MS);
        }
      }
    } finally {
      if (dupRunIdRef.current !== runId) return;
      setDupCountProgress({ running: false, current: 0, total: 0, currentGroupId: null });
    }

    addToast("Member counts updated for duplicate groups", {
      appearance: "success",
      autoDismiss: true,
    });
  };

  // Core logic to clear contacts for a single manager (no confirmation)
  const performClearContacts = async (row, options = {}) => {
    const { silent = false } = options;
    if (!row.employeeId) return { changed: false };

    const groupId = row.groupId;
    if (!groupId) {
      if (!silent) {
        addToast("Manager group not found", { appearance: "error", autoDismiss: true });
      }
      return { changed: false };
    }

    const knownCount = typeof row.existingMembersCount === "number" ? row.existingMembersCount : null;
    if (knownCount === 0) {
      if (!silent) addToast("No contacts to remove", { appearance: "info", autoDismiss: true });
      return { changed: false };
    }

    let members = [];
    try {
      members = await fetchAllGroupMemberIds(groupId);
    } catch (_) {
      members = [];
    }

    if (members.length === 0) {
      if (!silent) {
        addToast("No contacts to remove (could not load member list)", { appearance: "info", autoDismiss: true });
      }
      return { changed: false };
    }

    setRowActionState((prev) => ({
      ...prev,
      [row.employeeId]: { ...(prev[row.employeeId] || {}), clearing: true, lastAction: undefined },
    }));

    // Remove in chunks to avoid payload limits
    const batches = chunk(members, BULK_REMOVE_CHUNK_SIZE);
    for (let i = 0; i < batches.length; i += 1) {
      const batch = batches[i];
      // eslint-disable-next-line no-await-in-loop
      await API_BULK_UPDATE_ORGANIZATION_GROUP({
        organization_group_id: groupId,
        employee_ids: batch,
        action: "remove",
      });
      if (i < batches.length - 1) {
        // eslint-disable-next-line no-await-in-loop
        await delay(200);
      }
    }
    if (!silent) {
      addToast(`Cleared ${members.length} contact(s)`, { appearance: "success", autoDismiss: true });
    }

    // Optimistically update local count to 0 so the row is never blank/stale
    setRows((prev) =>
      (prev || []).map((r) =>
        r?.employeeId === row.employeeId ? { ...r, existingMembersCount: 0 } : r
      )
    );
    setManagerGroups((prev) => ({ ...prev, [row.employeeId]: { groupId, membersCount: 0 } }));
    setRowActionState((prev) => ({
      ...prev,
      [row.employeeId]: { ...(prev[row.employeeId] || {}), clearing: false, lastAction: 'cleared' },
    }));

    return { changed: true, count: members.length };
  };

  // Per-row handlers that include confirmation UX
  const handleAddContacts = async (row) => {
    if (!row.employeeId) return;
    const matchInfo = getMatchInfoForRow(row);
    if (matchInfo.loading) {
      addToast("Still fetching matching contacts, try again shortly", { appearance: "info", autoDismiss: true });
      return;
    }
    if ((matchInfo.count || 0) === 0) {
      addToast("No matching contacts found", { appearance: "info", autoDismiss: true });
      return;
    }
    const manager = getManagerById(row.employeeId);
    const confirmed = await new Promise((resolve) => {
      setConfirmDialog({
        open: true,
        type: 'add',
        manager,
        message: `Add ${matchInfo.count} contacts to ${
          manager ? `${manager.first_name || ''} ${manager.last_name || ''}`.trim() || manager.email : 'this manager'
        }?`,
        onConfirm: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });
    if (!confirmed) return;

    await performAddContacts(row);
  };

  const handleClearContacts = async (row) => {
    const groupId = row.groupId;
    if (!groupId) {
      addToast("Manager group not found", { appearance: "error", autoDismiss: true });
      return;
    }
    const knownCount = typeof row.existingMembersCount === "number" ? row.existingMembersCount : null;
    if (knownCount === 0) {
      addToast("No contacts to remove", { appearance: "info", autoDismiss: true });
      return;
    }
    const manager = getManagerById(row.employeeId);
    const confirmed = await new Promise((resolve) => {
      setConfirmDialog({
        open: true,
        type: 'clear',
        manager,
        message: `Clear ${
          typeof knownCount === "number" ? knownCount : "all"
        } contact(s) from ${
          manager ? `${manager.first_name || ''} ${manager.last_name || ''}`.trim() || manager.email : 'this manager'
        }?`,
        onConfirm: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });
    if (!confirmed) return;

    await performClearContacts(row);
  };

  // Bulk handlers
  const handleBulkAddContacts = async () => {
    if (bulkState.adding || bulkState.clearing) return;

    const candidates = getBulkAddCandidateRows();
    if (candidates.length === 0) {
      addToast("No managers with contacts to add", { appearance: "info", autoDismiss: true });
      return;
    }

    const confirmed = await new Promise((resolve) => {
      setConfirmDialog({
        open: true,
        type: 'bulk-add',
        manager: null,
        message: `Add matching contacts for ${candidates.length} manager(s)?`,
        onConfirm: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });
    if (!confirmed) return;

    setBulkState((prev) => ({ ...prev, adding: true }));
    setBulkAddProgress({
      running: true,
      current: 0,
      total: candidates.length,
      currentEmail: "",
    });
    let affectedManagers = 0;

    try {
      for (let i = 0; i < candidates.length; i++) {
        const row = candidates[i];
        setBulkAddProgress({
          running: true,
          current: i + 1,
          total: candidates.length,
          currentEmail: row?.email || "",
        });
        try {
          // eslint-disable-next-line no-await-in-loop
          const result = await performAddContacts(row, { silent: true });
          if (result?.changed) {
            affectedManagers += 1;
          }
        } catch (_) {
          // Keep going even if one row fails
        }

        if (i < candidates.length - 1) {
          // eslint-disable-next-line no-await-in-loop
          await delay(BULK_ADD_THROTTLE_MS);
        }
      }
    } finally {
      setBulkState((prev) => ({ ...prev, adding: false }));
      setBulkAddProgress({ running: false, current: 0, total: 0, currentEmail: "" });
    }

    addToast(
      `Completed add for ${affectedManagers} of ${candidates.length} manager(s)`,
      { appearance: "success", autoDismiss: true }
    );
  };

  const handleBulkClearContacts = async () => {
    if (bulkState.adding || bulkState.clearing) return;

    const candidates = getBulkClearCandidateRows();
    if (candidates.length === 0) {
      addToast("No managers with contacts to clear", { appearance: "info", autoDismiss: true });
      return;
    }

    const confirmed = await new Promise((resolve) => {
      setConfirmDialog({
        open: true,
        type: 'bulk-clear',
        manager: null,
        message: `Clear contacts for ${candidates.length} manager(s)?`,
        onConfirm: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });
    if (!confirmed) return;

    setBulkState((prev) => ({ ...prev, clearing: true }));
    let affectedManagers = 0;

    for (const row of candidates) {
      try {
        const result = await performClearContacts(row, { silent: true });
        if (result?.changed) {
          affectedManagers += 1;
        }
      } catch (_) {
        // Keep going even if one row fails
      }
    }

    setBulkState((prev) => ({ ...prev, clearing: false }));

    addToast(
      `Completed clear for ${affectedManagers} of ${candidates.length} manager(s)`,
      { appearance: "success", autoDismiss: true }
    );
  };

  const resetState = () => {
    if (isProcessing) return;
    setStep(1);
    setRawText("");
    setRows([]);
    setManagerGroups({});
    setProcessProgress({ current: 0, total: 0, currentEmail: "" });
  };

  const getManagerById = (id) => (employees || []).find((e) => e.id === id);

  // Resolve effective permissions for a given employee by combining backend fields with local overrides
  const getEffectivePermissionsForEmployee = (employeeId) => {
    if (!employeeId) {
      return {
        hasLogin: false,
        noraAI: false,
        readFeedback: false,
        accessAnalytics: false,
        pdfReports: false,
        specialPermission: false,
        analyticsPermissions: createAnalyticsPermissionState(false),
        dirty: false,
      };
    }

    const emp = (employees || []).find((e) => e.id === employeeId);
    const overrides = permissionState[employeeId] || {};

    const coreHasLogin =
      emp &&
      (emp.enable_login === true ||
        emp.enable_login === 1 ||
        emp.enable_login === "1");
    const coreAccessAnalytics = emp && emp.access_data_analytics === 1;
    const coreReadFeedback = emp && emp.read_feedback === 1;
    const corePdfReports = false; // default unless found in details
    const permissionList = employeePermission?.employee_permission || [];
    const permRecord = permissionList.find(
      (p) => p?.employee_id === Number(employeeId)
    );
    const details = permRecord
      ? (permRecord?.updated || permRecord?.created || permRecord)?.auth_details ||
        (permRecord?.updated || permRecord?.created || permRecord)?.details ||
        []
      : [];

    let coreNora = false;
    let coreSpecial = false;
    let parsedAnalytics = null;
    let hasPdf = corePdfReports;

    (details || []).forEach((d) => {
      if (
        d?.value === "nora_ai" ||
        d?.name === "nora_ai" ||
        d?.name_id === "nora_ai"
      ) {
        coreNora = d?.response !== false && d?.value !== "false";
      }
      if (
        d?.value === "special_permission" ||
        d?.name === "special_permission" ||
        d?.name_id === "special_permission"
      ) {
        coreSpecial = true;
      }
      if (
        d?.name === "pdf_enabled" ||
        d?.name_id === "pdf" ||
        d?.value === "PDF - Enabled"
      ) {
        hasPdf = true;
      }
    });

    parsedAnalytics = parseAnalyticsPermissionsFromDetails(details);
    const baseAnalyticsState = coreAccessAnalytics
      ? parsedAnalytics || createAnalyticsPermissionState(true)
      : createAnalyticsPermissionState(false);
    const analyticsPermissions =
      overrides.analyticsPermissions || baseAnalyticsState;

    return {
      hasLogin: overrides.hasLogin ?? Boolean(coreHasLogin),
      noraAI: overrides.noraAI ?? Boolean(coreNora),
      readFeedback: overrides.readFeedback ?? Boolean(coreReadFeedback),
      accessAnalytics: overrides.accessAnalytics ?? Boolean(coreAccessAnalytics),
      pdfReports: overrides.pdfReports ?? Boolean(hasPdf),
      specialPermission: overrides.specialPermission ?? Boolean(coreSpecial),
      analyticsPermissions,
      dirty: overrides.dirty || false,
    };
  };

  const updatePermissionState = (employeeId, updater) => {
    if (!employeeId) return;
    setPermissionState((prev) => {
      const existing = prev[employeeId] || {};
      const next = updater(existing);
      return {
        ...prev,
        [employeeId]: {
          ...existing,
          ...next,
          dirty: true,
        },
      };
    });
  };

  const handleTogglePermissionRow = (employeeId, key, value) => {
    updatePermissionState(employeeId, (existing) => ({
      ...existing,
      [key]: value,
      analyticsPermissions:
        key === "accessAnalytics"
          ? value
            ? existing.analyticsPermissions || createAnalyticsPermissionState(true)
            : createAnalyticsPermissionState(false)
          : existing.analyticsPermissions,
    }));
  };

  const handleToggleAnalyticsCard = (employeeId, cardKey, value) => {
    updatePermissionState(employeeId, (existing) => ({
      ...existing,
      analyticsPermissions: {
        ...(existing.analyticsPermissions || {}),
        [cardKey]: value,
      },
    }));
  };

  const handleTogglePermissionAll = (key, targetValue) => {
    setPermissionState((prev) => {
      const next = { ...prev };
      (rows || []).forEach((row) => {
        if (!row.employeeId) return;
        const existing = prev[row.employeeId] || {};
        next[row.employeeId] = {
          ...existing,
          [key]: targetValue,
          dirty: true,
        };
      });
      return next;
    });
  };

  const updateBulkPreset = (key, value) => {
    setBulkPreset((prev) => ({
      ...prev,
      [key]: value,
      analyticsPermissions:
        key === "accessAnalytics"
          ? value
            ? prev.analyticsPermissions && Object.values(prev.analyticsPermissions).some(Boolean)
              ? prev.analyticsPermissions
              : createAnalyticsPermissionState(true)
            : createAnalyticsPermissionState(false)
          : prev.analyticsPermissions,
    }));
  };

  const updateBulkPresetAnalytics = (cardKey, value) => {
    setBulkPreset((prev) => ({
      ...prev,
      analyticsPermissions: {
        ...(prev.analyticsPermissions || {}),
        [cardKey]: value,
      },
    }));
  };

  const applyBulkPresetToRows = () => {
    const eligible = (rows || []).filter((r) => r.employeeId);
    if (!eligible.length) {
      addToast("No managers to apply preset to", {
        appearance: "info",
        autoDismiss: true,
      });
      return;
    }
    setPermissionState((prev) => {
      const next = { ...prev };
      eligible.forEach((row) => {
        const existing = prev[row.employeeId] || {};
        const analyticsStateForRow =
          bulkPreset.accessAnalytics &&
          bulkPreset.analyticsPermissions &&
          Object.keys(bulkPreset.analyticsPermissions).length > 0
            ? bulkPreset.analyticsPermissions
            : bulkPreset.accessAnalytics
            ? createAnalyticsPermissionState(true)
            : createAnalyticsPermissionState(false);
        next[row.employeeId] = {
          ...existing,
          hasLogin: bulkPreset.hasLogin,
          noraAI: bulkPreset.noraAI,
          readFeedback: bulkPreset.readFeedback,
          accessAnalytics: bulkPreset.accessAnalytics,
          pdfReports: bulkPreset.pdfReports,
          specialPermission: bulkPreset.specialPermission,
          analyticsPermissions: analyticsStateForRow,
          dirty: true,
        };
      });
      return next;
    });
    addToast("Applied preset to all managers in the table", {
      appearance: "success",
      autoDismiss: true,
    });
  };

  const handleBulkSavePermissions = async () => {
    if (permissionBulkState.saving) return;

    const dirtyManagers = (rows || [])
      .filter((r) => r.employeeId && permissionState[r.employeeId]?.dirty)
      .map((r) => r.employeeId);

    if (!dirtyManagers.length) {
      addToast("No permission changes to save", {
        appearance: "info",
        autoDismiss: true,
      });
      return;
    }

    setPermissionBulkState({
      saving: true,
      current: 0,
      total: dirtyManagers.length,
      currentEmail: "",
    });

    const orgId =
      get_auth?.organization_id || get_auth?.selectedOrganization?.id;

    let processedCount = 0;

    for (const employeeId of dirtyManagers) {
      const manager = getManagerById(employeeId);
      if (!manager) {
        // Skip if we somehow lost the manager record
        processedCount += 1;
        setPermissionBulkState((prev) => ({
          ...prev,
          current: processedCount,
        }));
        // eslint-disable-next-line no-await-in-loop
        await delay(PERMISSION_BULK_THROTTLE_MS);
        continue;
      }

      try {
        setPermissionBulkState((prev) => ({
          ...prev,
          currentEmail: manager?.email || "",
        }));

        const effective = getEffectivePermissionsForEmployee(employeeId);
        const {
          hasLogin,
          noraAI,
          readFeedback,
          accessAnalytics,
          pdfReports,
          specialPermission,
          analyticsPermissions,
        } = effective;

        // Persist core audit-style fields twice:
        // 1) Via the culture audit endpoint (to mirror EditAudit/ManagerPermissions behaviour)
        // 2) Via the canonical employee endpoint so ManagerPermissions' GET
        //    (which uses /employee/:id/) reflects the same flags.
        const coreAuditPayload = {
          id: manager.id,
          first_name: manager.first_name,
          last_name: manager.last_name,
          enable_login: hasLogin === true,
          read_feedback: readFeedback ? 1 : 0,
          access_data_analytics: accessAnalytics ? 1 : 0,
          // Always keep access_subfactor_data enabled (hidden from UI) to
          // mirror ManagerPermissions.js behaviour.
          access_subfactor_data: 1,
          // Preserve existing audit fields for role / scheduling so we only
          // change the visible permissions here.
          survey_add_users:
            typeof manager.survey_add_users === "number"
              ? manager.survey_add_users
              : manager.survey_add_users
              ? 1
              : 0,
          account_type: manager.account_type,
          survey_schedule:
            typeof manager.survey_schedule === "number"
              ? manager.survey_schedule
              : manager.survey_schedule
              ? 1
              : 0,
          // Always keep participation enabled (hidden from UI) to mirror
          // ManagerPermissions.js behaviour.
          survey_status: 1,
        };

        // Persist core fields sequentially (await) to avoid flooding the API in bulk saves.
        // 1) Culture audit endpoint (source=culture)
        // 2) Canonical employee endpoint (no source=culture)
        // eslint-disable-next-line no-await-in-loop
        await API_EDIT_EMPLOYEE_AUDIT(coreAuditPayload);
        // eslint-disable-next-line no-await-in-loop
        await API_EDIT_EMPLOYEE(coreAuditPayload);

        // Persist Nora AI detailed permission
        let existing = null;
        // Prefer fetching by employee (+ org) to avoid pagination issues when we previously loaded by organization.
        try {
          // eslint-disable-next-line no-await-in-loop
          const res = await API_GET_EMPLOYEE_PERMISSIONS(
            `employee=${Number(employeeId)}${orgId ? `&organization=${orgId}` : ""}`
          );
          const list = res?.data?.results || res?.data || [];
          existing = Array.isArray(list) ? list[0] : null;
        } catch (_) {
          // Fall back to whatever we have in Redux (may be incomplete if org list is paginated)
          const permissionList = employeePermission?.employee_permission || [];
          existing = permissionList.find((p) => p?.employee_id === Number(employeeId)) || null;
        }

        const existingBase = existing?.updated || existing?.created || existing;
        const existingDetails =
          (existingBase?.auth_details || existingBase?.details || []).filter(
            (d) =>
              d?.value !== "nora_ai" &&
              d?.name !== "nora_ai" &&
              d?.name_id !== "nora_ai" &&
              d?.value !== "PDF - Enabled" &&
              d?.name !== "pdf_enabled" &&
              d?.name_id !== "pdf" &&
              d?.value !== "special_permission" &&
              d?.name !== "special_permission" &&
              d?.name_id !== "special_permission" &&
              !isAnalyticsAuthDetail(d)
          );
        const updatedDetails = [...existingDetails];

        if (pdfReports) {
          updatedDetails.push({
            name_id: "pdf",
            name: "pdf_enabled",
            value_id: "pdf",
            value: "PDF - Enabled",
            text: "Enabled PDF",
          });
        }

        updatedDetails.push({
          value: noraAI ? "nora_ai" : "false",
          value_id: "nora_ai",
          name: "nora_ai",
          name_id: "nora_ai",
          response: noraAI,
          text: noraAI ? "Nora AI Access" : "Nora AI Access (Disabled)",
        });

        if (specialPermission) {
          updatedDetails.push({
            value: "special_permission",
            value_id: "special_permission",
            name: "special_permission",
            name_id: "special_permission",
            text: "Special Permission",
          });
        }

        if (accessAnalytics && analyticsPermissions) {
          const analyticsDetails = analyticsPermissionsToDetails(analyticsPermissions);
          analyticsDetails.forEach((detail) => {
            updatedDetails.push(detail);
          });
        }

        // Upsert employee-permission (awaited) to avoid "already exists" errors and to keep the bulk save truly sequential.
        const existingId = existing?.id || existingBase?.id;
        if (existingId) {
          // eslint-disable-next-line no-await-in-loop
          await API_EDIT_EMPLOYEE_PERMISSION({
            body: { id: existingId, auth_details: updatedDetails },
            query: orgId ? `organization=${orgId}` : "",
          });
        } else if (updatedDetails.length > 0) {
          // eslint-disable-next-line no-await-in-loop
          await API_ADD_EMPLOYEE_PERMISSION({
            body: { employee: Number(employeeId), auth_details: updatedDetails },
            query: orgId ? `organization=${orgId}` : "",
          });
        }

        setPermissionState((prev) => ({
          ...prev,
          [employeeId]: {
            ...(prev[employeeId] || {}),
            dirty: false,
          },
        }));
      } catch (e) {
        // Keep going even if one manager fails
        // eslint-disable-next-line no-console
        console.error("Error saving permissions for manager", employeeId, e);
      }

      processedCount += 1;
      setPermissionBulkState((prev) => ({
        ...prev,
        current: processedCount,
      }));
      // Throttle bulk saves (true network throttle, since we await requests above)
      // eslint-disable-next-line no-await-in-loop
      await delay(PERMISSION_BULK_THROTTLE_MS);
    }

    setPermissionBulkState({
      saving: false,
      current: 0,
      total: 0,
      currentEmail: "",
    });

    addToast(
      `Updated permissions for ${dirtyManagers.length} manager(s)`,
      { appearance: "success", autoDismiss: true }
    );

    // Refresh org-wide permissions snapshot for subsequent UI reads.
    if (orgId) {
      dispatch({
        type: SG_GET_EMPLOYEE_PERMISSIONS,
        payload: `organization=${orgId}`,
      });
    }
  };

  const toggleRowExpansion = (employeeId) => {
    if (!employeeId) return;
    setExpandedRows((prev) => ({
      ...prev,
      [employeeId]: !prev[employeeId],
    }));
  };

  const breadcrumbCrumbs = useMemo(
    () => [
      {
        label: "User Management",
        onClick: () => {
          if (typeof onBack === "function") {
            onBack();
          }
        },
      },
      {
        label: "Bulk Import",
      },
    ],
    [onBack]
  );

  const resolveManagerDisplay = (managerId) => {
    const emp = (employees || []).find((e) => Number(e?.id) === Number(managerId));
    const name = emp ? `${emp.first_name || ""} ${emp.last_name || ""}`.trim() : "";
    return {
      manager: emp || null,
      name: name || (emp?.email || ""),
      email: emp?.email || "",
    };
  };

  const scanDuplicatePermissionGroups = async () => {
    const orgId = get_auth?.organization_id || get_auth?.selectedOrganization?.id;
    if (!orgId) {
      addToast("No organization selected", { appearance: "warning", autoDismiss: true });
      return;
    }
    if (duplicateAudit.scanning) return;

    setDuplicateAudit((prev) => ({ ...prev, scanning: true, error: null }));
    try {
      const allGroups = await fetchAllPermissionGroupsForOrg(orgId);
      // Group by manager_id
      const byManager = new Map(); // managerId -> groups[]
      (allGroups || []).forEach((g) => {
        if (!g) return;
        const managerId = typeof g.manager === "object" ? g.manager?.id : g.manager;
        if (!managerId) return;
        const existing = byManager.get(Number(managerId)) || [];
        existing.push({ id: g.id, name: g.name });
        byManager.set(Number(managerId), existing);
      });

      const duplicates = [];
      byManager.forEach((groups, managerId) => {
        if (!Array.isArray(groups) || groups.length < 2) return;
        const info = resolveManagerDisplay(managerId);
        duplicates.push({
          managerId,
          managerEmail: info.email,
          managerName: info.name,
          groups: groups.filter((x) => x?.id),
        });
      });

      // Sort: most duplicates first, then by manager name/email
      duplicates.sort((a, b) => {
        const dc = (b?.groups?.length || 0) - (a?.groups?.length || 0);
        if (dc !== 0) return dc;
        return String(a?.managerName || "").localeCompare(String(b?.managerName || ""));
      });

      setDuplicateAudit({
        scanning: false,
        error: null,
        duplicates,
        lastScannedAt: Date.now(),
      });

      // Reset counts when a new scan runs so UI doesn't show stale numbers
      setDupCounts({});
      setDupCountProgress({ running: false, current: 0, total: 0, currentGroupId: null });

      addToast(
        duplicates.length
          ? `Found ${duplicates.length} manager(s) with duplicate groups`
          : "No duplicate groups found",
        { appearance: duplicates.length ? "warning" : "success", autoDismiss: true }
      );
    } catch (e) {
      setDuplicateAudit((prev) => ({
        ...prev,
        scanning: false,
        error: e?.response?.data || e?.message || true,
      }));
      addToast("Failed to scan for duplicates", { appearance: "error", autoDismiss: true });
    }
  };

  const clearDuplicateAudit = () => {
    cancelDuplicateCountRun();
    setDuplicateAudit({
      scanning: false,
      error: null,
      duplicates: [],
      lastScannedAt: null,
    });
    setDupCounts({});
  };

  const removeDuplicateGroupLocally = (groupId) => {
    if (!groupId) return;
    setDuplicateAudit((prev) => {
      const nextDups = (prev?.duplicates || [])
        .map((d) => ({
          ...d,
          groups: (d?.groups || []).filter((g) => Number(g?.id) !== Number(groupId)),
        }))
        .filter((d) => (d?.groups || []).length > 1);
      return { ...prev, duplicates: nextDups };
    });
    setDupCounts((prev) => {
      const next = { ...prev };
      delete next[groupId];
      return next;
    });
  };

  const handleDeleteDuplicateGroup = async ({ managerId, groupId, groupName }) => {
    if (!groupId) return;
    if (duplicateAudit.scanning || dupCountProgress.running) return;

    const info = resolveManagerDisplay(managerId);
    const count = dupCounts?.[groupId]?.count;
    const countSuffix =
      typeof count === "number"
        ? ` This group currently has ${count} contact(s).`
        : "";

    const confirmed = await new Promise((resolve) => {
      setConfirmDialog({
        open: true,
        type: "delete-duplicate-group",
        manager: null,
        message: `Delete group "${groupName || "Unnamed"}" (ID ${groupId}) for ${
          info?.name || info?.email || "this manager"
        }? This cannot be undone.${countSuffix}`,
        onConfirm: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });
    if (!confirmed) return;

    try {
      dispatch({ type: SG_DELETE_ORGANIZATION_GROUP, payload: { id: groupId } });
      removeDuplicateGroupLocally(groupId);
      addToast("Deleting duplicate group…", { appearance: "info", autoDismiss: true });
    } catch (_) {
      addToast("Failed to delete group", { appearance: "error", autoDismiss: true });
    }
  };

  const renderDuplicateAuditPanel = () => (
    <AuditCard>
      <AuditHeader>
        <div>
          <AuditTitle>Duplicate groups audit</AuditTitle>
          <AuditHint>
            Scans permission groups for duplicate managers (same manager_id with multiple groups). Use member counting to compare before deleting.
          </AuditHint>
        </div>
        <AuditActions>
          {dupCountProgress.running ? (
            <SecondaryMini onClick={cancelDuplicateCountRun}>
              Stop counting
            </SecondaryMini>
          ) : (
            <SecondaryMini
              onClick={clearDuplicateAudit}
              disabled={duplicateAudit.scanning}
            >
              Clear
            </SecondaryMini>
          )}
          <SecondaryMini
            onClick={scanDuplicatePermissionGroups}
            disabled={duplicateAudit.scanning || dupCountProgress.running}
          >
            {duplicateAudit.scanning ? "Scanning…" : "Scan duplicates"}
          </SecondaryMini>
          <PrimaryMini
            onClick={fetchDuplicateGroupMemberCounts}
            disabled={
              duplicateAudit.scanning ||
              dupCountProgress.running ||
              !(duplicateAudit?.duplicates || []).length
            }
          >
            {dupCountProgress.running ? "Counting…" : "Fetch member counts"}
          </PrimaryMini>
        </AuditActions>
      </AuditHeader>
      {duplicateAudit.error && (
        <AuditError>Failed to scan duplicates. Try again.</AuditError>
      )}
      {dupCountProgress.running && (
        <AuditProgressRow>
          Counting members ({dupCountProgress.current}/{dupCountProgress.total})
          {dupCountProgress.currentGroupId
            ? ` · Group ${dupCountProgress.currentGroupId}`
            : ""}
        </AuditProgressRow>
      )}
      {(duplicateAudit?.duplicates || []).length > 0 ? (
        <AuditTableWrap>
          <Table>
            <thead>
              <tr>
                <Th>Manager</Th>
                <Th>Email</Th>
                <Th>Group ID</Th>
                <Th>Group name</Th>
                <Th>Members</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {duplicateAudit.duplicates.map((d) =>
                (d.groups || []).map((g, idx) => {
                  const c = dupCounts?.[g.id];
                  const membersDisplay = c?.loading
                    ? "…"
                    : typeof c?.count === "number"
                    ? c.count
                    : "—";
                  const disableDelete =
                    duplicateAudit.scanning || dupCountProgress.running;
                  return (
                    <tr key={`${d.managerId}-${g.id}`}>
                      <Td>{idx === 0 ? d.managerName || d.managerId : ""}</Td>
                      <Td>{idx === 0 ? d.managerEmail || "—" : ""}</Td>
                      <Td>{g.id}</Td>
                      <Td>{g.name || "—"}</Td>
                      <Td>{membersDisplay}</Td>
                      <Td>
                        <RowActions>
                          <DangerMini
                            onClick={() =>
                              handleDeleteDuplicateGroup({
                                managerId: d.managerId,
                                groupId: g.id,
                                groupName: g.name,
                              })
                            }
                            disabled={disableDelete}
                          >
                            Delete group
                          </DangerMini>
                        </RowActions>
                      </Td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>
        </AuditTableWrap>
      ) : (
        <AuditEmpty>
          {duplicateAudit.scanning
            ? "Scanning permission groups…"
            : "No duplicates loaded yet. Run “Scan duplicates” to audit this organization."}
        </AuditEmpty>
      )}
    </AuditCard>
  );

  const processProgressPercent =
    processProgress?.total > 0
      ? Math.min(100, Math.max(0, Math.round((processProgress.current / processProgress.total) * 100)))
      : 0;
  const processRemainingSeconds =
    processProgress?.total > 0
      ? Math.max(
          0,
          Math.ceil(((processProgress.total - processProgress.current) * STEP1_THROTTLE_MS) / 1000)
        )
      : 0;

  if (showScanner) {
    return (
      <ManagerGroupParticipationScanner onBack={() => setShowScanner(false)} />
    );
  }

  return (
    <PageContainer>
      {permissionBulkState.saving && (
        <BlockingOverlay role="dialog" aria-modal="true">
          <BlockingCard>
            <BlockingTitle>Saving permissions…</BlockingTitle>
            <BlockingSubtext>
              {permissionBulkState.current}/{permissionBulkState.total}
              {permissionBulkState.currentEmail ? ` · ${permissionBulkState.currentEmail}` : ""}
            </BlockingSubtext>
            <BlockingBar>
              <BlockingBarFill
                style={{
                  width:
                    permissionBulkState.total > 0
                      ? `${Math.min(
                          100,
                          Math.max(
                            0,
                            Math.round((permissionBulkState.current / permissionBulkState.total) * 100)
                          )
                        )}%`
                      : "0%",
                }}
              />
            </BlockingBar>
            <BlockingHint>
              Saving one manager at a time to avoid API throttling. Please keep this tab open.
            </BlockingHint>
          </BlockingCard>
        </BlockingOverlay>
      )}
      {bulkAddProgress.running && (
        <BlockingOverlay role="dialog" aria-modal="true">
          <BlockingCard>
            <BlockingTitle>Adding contacts…</BlockingTitle>
            <BlockingSubtext>
              {bulkAddProgress.current}/{bulkAddProgress.total}
              {bulkAddProgress.currentEmail ? ` · ${bulkAddProgress.currentEmail}` : ""}
            </BlockingSubtext>
            <BlockingBar>
              <BlockingBarFill
                style={{
                  width:
                    bulkAddProgress.total > 0
                      ? `${Math.min(
                          100,
                          Math.max(
                            0,
                            Math.round((bulkAddProgress.current / bulkAddProgress.total) * 100)
                          )
                        )}%`
                      : "0%",
                }}
              />
            </BlockingBar>
            <BlockingHint>
              Please keep this tab open. This may take a few minutes for large imports.
            </BlockingHint>
          </BlockingCard>
        </BlockingOverlay>
      )}
      <AccessBreadcrumb includeRoot={false} crumbs={breadcrumbCrumbs} />
      <PageHeader>
        <HeaderText>
          <Title>Bulk Import Users</Title>
          <Subtitle>Create or verify users, then sync access contacts by tags</Subtitle>
        </HeaderText>
        <HeaderActions>
          <SecondaryButton
            onClick={() => setShowScanner(true)}
            disabled={isProcessing || permissionBulkState.saving || bulkAddProgress.running}
          >
            Response threshold scanner
          </SecondaryButton>
        </HeaderActions>
      </PageHeader>

      <Content>
        {step === 1 && (
          <>
            <Instructions>
              Paste one entry per line as CSV: email, group name, tags
              <SmallHint>
                Group name is optional; if left blank it will default to the manager&apos;s name. Tags can be multiple, separated by commas (e.g. Sales, Marketing, East). Lines starting with # are ignored.
              </SmallHint>
            </Instructions>
            <Textarea
              placeholder={`manager@company.com, Leadership Team, Sales, Marketing\nexisting@company.com, , Toronto, East`}
              value={rawText}
              onChange={(e) => setRawText(e.target.value)}
              disabled={isProcessing}
            />
            {renderDuplicateAuditPanel()}
            {isProcessing && processProgress.total > 0 && (
              <ProcessProgressWrap>
                <ProcessProgressLine>
                  Processing ({processProgress.current}/{processProgress.total})
                  {processProgress.currentEmail ? `: ${processProgress.currentEmail}` : ""}
                </ProcessProgressLine>
                <ProcessProgressSubline>
                  {processProgressPercent}% · ~{processRemainingSeconds}s remaining
                </ProcessProgressSubline>
                <ProgressBar>
                  <ProgressBarFill style={{ width: `${processProgressPercent}%` }} />
                </ProgressBar>
              </ProcessProgressWrap>
            )}
            <Actions>
              <SecondaryButton onClick={() => { resetState(); onBack && onBack(); }}>Cancel</SecondaryButton>
              <PrimaryButton onClick={handleProcessAndContinue} disabled={!rawText.trim() || isProcessing}>
                {isProcessing ? 'Processing…' : 'Process & Continue'}
              </PrimaryButton>
            </Actions>
          </>
        )}

        {step === 2 && (
          <>
            <StepHeaderRow>
              <StepHeader>Managers & Matching Contacts</StepHeader>
              <BulkActions>
                <PrimaryMini
                  onClick={handleBulkSavePermissions}
                  disabled={
                    permissionBulkState.saving ||
                    !rows.some(
                      (r) =>
                        r.employeeId && permissionState[r.employeeId]?.dirty
                    )
                  }
                >
                  {permissionBulkState.saving
                    ? `Saving… (${permissionBulkState.current}/${permissionBulkState.total})`
                    : "Save permissions"}
                </PrimaryMini>
                {permissionBulkState.saving && (
                  <SavingText>
                    Saving permissions ({permissionBulkState.current} of{" "}
                    {permissionBulkState.total} managers)…
                  </SavingText>
                )}
                <PrimaryMini
                  onClick={handleBulkAddContacts}
                  disabled={bulkState.adding || bulkState.clearing || getBulkAddCandidateRows().length === 0}
                >
                  {bulkState.adding ? 'Adding…' : 'Add all contacts'}
                </PrimaryMini>
                <DangerMini
                  onClick={handleBulkClearContacts}
                  disabled={bulkState.adding || bulkState.clearing || getBulkClearCandidateRows().length === 0}
                >
                  {bulkState.clearing ? 'Clearing…' : 'Clear all contacts'}
                </DangerMini>
              </BulkActions>
            </StepHeaderRow>
            {previewProgress.running && (
              <PreviewStatusRow>
                Fetching match previews ({previewProgress.currentManager}/{previewProgress.totalManagers})
                {previewProgress.currentEmail ? `: ${previewProgress.currentEmail}` : ""}
                {previewProgress.anchorsTotal > 0
                  ? ` · tags ${previewProgress.anchorsCompleted}/${previewProgress.anchorsTotal}`
                  : ""}
              </PreviewStatusRow>
            )}
            {renderDuplicateAuditPanel()}
            <PresetCard>
              <PresetHeader>
                <div>
                  <PresetTitle>Permission preset</PresetTitle>
                  <PresetHint>Configure once, then apply to all managers in this table.</PresetHint>
                </div>
                <PresetActions>
                  <SecondaryMini
                    onClick={() => {
                      setBulkPreset({
                        hasLogin: false,
                        noraAI: false,
                        readFeedback: false,
                        accessAnalytics: false,
                        pdfReports: false,
                        specialPermission: false,
                        analyticsPermissions: createAnalyticsPermissionState(false),
                      });
                    }}
                  >
                    Reset
                  </SecondaryMini>
                  <PrimaryMini onClick={applyBulkPresetToRows}>
                    Apply to all
                  </PrimaryMini>
                </PresetActions>
              </PresetHeader>
              <PresetGrid>
                <PresetItem>
                  <PresetLabel>Login</PresetLabel>
                  <PermissionCheckboxInput
                    checked={bulkPreset.hasLogin}
                    onChange={() => updateBulkPreset("hasLogin", !bulkPreset.hasLogin)}
                  />
                </PresetItem>
                <PresetItem>
                  <PresetLabel>Nora AI</PresetLabel>
                  <PermissionCheckboxInput
                    checked={bulkPreset.noraAI}
                    onChange={() => updateBulkPreset("noraAI", !bulkPreset.noraAI)}
                  />
                </PresetItem>
                <PresetItem>
                  <PresetLabel>Read feedback</PresetLabel>
                  <PermissionCheckboxInput
                    checked={bulkPreset.readFeedback}
                    onChange={() =>
                      updateBulkPreset("readFeedback", !bulkPreset.readFeedback)
                    }
                  />
                </PresetItem>
                <PresetItem>
                  <PresetLabel>Access analytics</PresetLabel>
                  <PermissionCheckboxInput
                    checked={bulkPreset.accessAnalytics}
                    onChange={() =>
                      updateBulkPreset("accessAnalytics", !bulkPreset.accessAnalytics)
                    }
                  />
                </PresetItem>
                <PresetItem>
                  <PresetLabel>PDF reports</PresetLabel>
                  <PermissionCheckboxInput
                    checked={bulkPreset.pdfReports}
                    onChange={() =>
                      updateBulkPreset("pdfReports", !bulkPreset.pdfReports)
                    }
                    disabled={!bulkPreset.hasLogin}
                  />
                </PresetItem>
                <PresetItem>
                  <PresetLabel>Special permission</PresetLabel>
                  <PermissionCheckboxInput
                    checked={bulkPreset.specialPermission}
                    onChange={() =>
                      updateBulkPreset("specialPermission", !bulkPreset.specialPermission)
                    }
                  />
                </PresetItem>
              </PresetGrid>
              {bulkPreset.accessAnalytics ? (
                <PresetAnalyticsGrid>
                  {ANALYTICS_CARD_PERMISSIONS.map((card) => (
                    <PresetAnalyticsItem key={card.key}>
                      <div>
                        <PresetLabel>{card.label}</PresetLabel>
                        <PresetSubtext>{card.description}</PresetSubtext>
                      </div>
                      <PermissionCheckboxInput
                        checked={bulkPreset.analyticsPermissions?.[card.key]}
                        onChange={() =>
                          updateBulkPresetAnalytics(
                            card.key,
                            !bulkPreset.analyticsPermissions?.[card.key]
                          )
                        }
                      />
                    </PresetAnalyticsItem>
                  ))}
                </PresetAnalyticsGrid>
              ) : (
                <PresetHint>Enable Access analytics to configure dashboards.</PresetHint>
              )}
            </PresetCard>
            <Table>
              <thead>
                <tr>
                  <Th>Manager</Th>
                  <Th>Email</Th>
                  <Th>Anchors</Th>
                  <Th>Matches</Th>
                  <PermissionTh>
                    <PermissionHeaderContent>
                      <span>Login</span>
                      <PermissionCheckboxInput
                        checked={
                          rows.length > 0 &&
                          rows
                            .filter((r) => r.employeeId)
                            .every(
                              (r) =>
                                getEffectivePermissionsForEmployee(
                                  r.employeeId
                                ).hasLogin
                            )
                        }
                        disabled={permissionBulkState.saving || rows.length === 0}
                        onChange={() => {
                          const eligible = rows.filter((r) => r.employeeId);
                          if (!eligible.length) return;
                          const allChecked = eligible.every(
                            (r) =>
                              getEffectivePermissionsForEmployee(
                                r.employeeId
                              ).hasLogin
                          );
                          handleTogglePermissionAll("hasLogin", !allChecked);
                        }}
                      />
                    </PermissionHeaderContent>
                  </PermissionTh>
                  <PermissionTh>
                    <PermissionHeaderContent>
                      <span>Nora AI</span>
                      <PermissionCheckboxInput
                        checked={
                          rows.length > 0 &&
                          rows
                            .filter((r) => r.employeeId)
                            .every(
                              (r) =>
                                getEffectivePermissionsForEmployee(
                                  r.employeeId
                                ).noraAI
                            )
                        }
                        disabled={permissionBulkState.saving || rows.length === 0}
                        onChange={() => {
                          const eligible = rows.filter((r) => r.employeeId);
                          if (!eligible.length) return;
                          const allChecked = eligible.every(
                            (r) =>
                              getEffectivePermissionsForEmployee(
                                r.employeeId
                              ).noraAI
                          );
                          handleTogglePermissionAll("noraAI", !allChecked);
                        }}
                      />
                    </PermissionHeaderContent>
                  </PermissionTh>
                  <PermissionTh>
                    <PermissionHeaderContent>
                      <span>Read</span>
                      <PermissionCheckboxInput
                        checked={
                          rows.length > 0 &&
                          rows
                            .filter((r) => r.employeeId)
                            .every(
                              (r) =>
                                getEffectivePermissionsForEmployee(
                                  r.employeeId
                                ).readFeedback
                            )
                        }
                        disabled={permissionBulkState.saving || rows.length === 0}
                        onChange={() => {
                          const eligible = rows.filter((r) => r.employeeId);
                          if (!eligible.length) return;
                          const allChecked = eligible.every(
                            (r) =>
                              getEffectivePermissionsForEmployee(
                                r.employeeId
                              ).readFeedback
                          );
                          handleTogglePermissionAll("readFeedback", !allChecked);
                        }}
                      />
                    </PermissionHeaderContent>
                  </PermissionTh>
                  <PermissionTh>
                    <PermissionHeaderContent>
                      <span>Analytics</span>
                      <PermissionCheckboxInput
                        checked={
                          rows.length > 0 &&
                          rows
                            .filter((r) => r.employeeId)
                            .every(
                              (r) =>
                                getEffectivePermissionsForEmployee(
                                  r.employeeId
                                ).accessAnalytics
                            )
                        }
                        disabled={permissionBulkState.saving || rows.length === 0}
                        onChange={() => {
                          const eligible = rows.filter((r) => r.employeeId);
                          if (!eligible.length) return;
                          const allChecked = eligible.every(
                            (r) =>
                              getEffectivePermissionsForEmployee(
                                r.employeeId
                              ).accessAnalytics
                          );
                          handleTogglePermissionAll(
                            "accessAnalytics",
                            !allChecked
                          );
                        }}
                      />
                    </PermissionHeaderContent>
                  </PermissionTh>
                  <Th>Actions</Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, idx) => {
                  const manager = r.employeeId ? getManagerById(r.employeeId) : null;
                  const matchInfo = getMatchInfoForRow(r);
                  const permissions = getEffectivePermissionsForEmployee(
                    r.employeeId
                  );
                  const analyticsPermissions =
                    permissions.analyticsPermissions || createAnalyticsPermissionState(false);
                  const isExpanded = !!expandedRows[r.employeeId];
                  const permissionsDisabled =
                    !r.employeeId || permissionBulkState.saving;
                  return (
                    <React.Fragment key={idx}>
                      <tr>
                        <Td>{manager ? `${manager.first_name || ''} ${manager.last_name || ''}`.trim() || manager.email : r.email}</Td>
                        <Td>{manager ? manager.email : r.email}</Td>
                        <Td>
                          {(!r.anchors || r.anchors.length === 0) ? (
                            '—'
                          ) : (
                            <AnchorsCell>
                              <AnchorsInlineList>
                                {r.anchors.slice(0, 4).map((anchor, index) => (
                                  <AnchorChip key={`${anchor}-${index}`}>{anchor}</AnchorChip>
                                ))}
                              </AnchorsInlineList>
                              {r.anchors.length > 4 && (
                                <AnchorsOverflowButton
                                  type="button"
                                  onClick={() => toggleRowExpansion(r.employeeId)}
                                  disabled={!r.employeeId}
                                >
                                  +{r.anchors.length - 4} more
                                </AnchorsOverflowButton>
                              )}
                              <AnchorsMeta>
                                {r.anchors.length} anchor{r.anchors.length === 1 ? '' : 's'}
                              </AnchorsMeta>
                            </AnchorsCell>
                          )}
                        </Td>
                        <Td>
                          {matchInfo.loading ? (
                            matchInfo.totalFilters > 0 ? (
                              `${matchInfo.completedFilters}/${matchInfo.totalFilters}`
                            ) : (
                              '…'
                            )
                          ) : (
                            matchInfo.count
                          )}
                        </Td>
                        <PermissionTd>
                          <PermissionCheckboxInput
                            checked={permissions.hasLogin}
                            disabled={permissionsDisabled}
                            onChange={() =>
                              handleTogglePermissionRow(
                                r.employeeId,
                                "hasLogin",
                                !permissions.hasLogin
                              )
                            }
                          />
                        </PermissionTd>
                        <PermissionTd>
                          <PermissionCheckboxInput
                            checked={permissions.noraAI}
                            disabled={permissionsDisabled}
                            onChange={() =>
                              handleTogglePermissionRow(
                                r.employeeId,
                                "noraAI",
                                !permissions.noraAI
                              )
                            }
                          />
                        </PermissionTd>
                        <PermissionTd>
                          <PermissionCheckboxInput
                            checked={permissions.readFeedback}
                            disabled={permissionsDisabled}
                            onChange={() =>
                              handleTogglePermissionRow(
                                r.employeeId,
                                "readFeedback",
                                !permissions.readFeedback
                              )
                            }
                          />
                        </PermissionTd>
                        <PermissionTd>
                          <PermissionCheckboxInput
                            checked={permissions.accessAnalytics}
                            disabled={permissionsDisabled}
                            onChange={() =>
                              handleTogglePermissionRow(
                                r.employeeId,
                                "accessAnalytics",
                                !permissions.accessAnalytics
                              )
                            }
                          />
                        </PermissionTd>
                        <Td>
                          <RowActions>
                            {(() => {
                              const s = rowActionState[r.employeeId] || {};
                              const addDisabled =
                                !r.employeeId ||
                                matchInfo.loading ||
                                matchInfo.count === 0 ||
                                s.adding ||
                                s.clearing ||
                                s.lastAction === 'added' ||
                                bulkState.adding ||
                                bulkState.clearing;
                              const addLabel = s.adding
                                ? 'Adding…'
                                : s.lastAction === 'added'
                                ? 'Added'
                                : matchInfo.loading
                                ? 'Loading…'
                                : `Add ${matchInfo.count} contacts`;
                              return (
                                <PrimaryMini onClick={() => handleAddContacts(r)} disabled={addDisabled}>
                                  {addLabel}
                                </PrimaryMini>
                              );
                            })()}
                            {r.isManager && r.existedBefore && (() => {
                              const s = rowActionState[r.employeeId] || {};
                              const currentCount =
                                typeof r.existingMembersCount === "number"
                                  ? r.existingMembersCount
                                  : typeof managerGroups?.[r.employeeId]?.membersCount === "number"
                                  ? managerGroups[r.employeeId].membersCount
                                  : 0;
                              const clearDisabled =
                                s.adding ||
                                s.clearing ||
                                !!s.lastAction ||
                                bulkState.adding ||
                                bulkState.clearing;
                              const clearLabel =
                                s.clearing
                                  ? 'Clearing…'
                                  : s.lastAction === 'cleared'
                                  ? 'Cleared'
                                  : typeof currentCount === 'number' && currentCount > 0
                                  ? `Clear ${currentCount} contact${currentCount === 1 ? '' : 's'}`
                                  : 'Clear contacts';
                              return (
                                <DangerMini onClick={() => handleClearContacts(r)} disabled={clearDisabled}>
                                  {clearLabel}
                                </DangerMini>
                              );
                            })()}
                            <GhostMini
                              onClick={() => toggleRowExpansion(r.employeeId)}
                              disabled={!r.employeeId}
                            >
                              {isExpanded ? "Hide details" : "Details"}
                            </GhostMini>
                          </RowActions>
                        </Td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <ExpandedTd colSpan={9}>
                            <ExpandedSection>
                              <ExpandedHeader>
                                <ExpandedTitle>Granular permissions</ExpandedTitle>
                                <ExpandedHint>
                                  Adjust per-manager settings without crowding the main table.
                                </ExpandedHint>
                              </ExpandedHeader>
                              {Array.isArray(r.anchors) && r.anchors.length > 0 && (
                                <ExpandedAnchorsBlock>
                                  <ExpandedAnchorsTitle>Anchors for this manager</ExpandedAnchorsTitle>
                                  <ExpandedAnchorsList>
                                    {r.anchors.map((anchor, index) => (
                                      <AnchorChip key={`${anchor}-${index}`}>{anchor}</AnchorChip>
                                    ))}
                                  </ExpandedAnchorsList>
                                </ExpandedAnchorsBlock>
                              )}
                              <PermissionGrid>
                                <PermissionGridItem>
                                  <span>PDF reports</span>
                                  <PermissionCheckboxInput
                                    checked={permissions.pdfReports}
                                    disabled={permissionsDisabled || !permissions.hasLogin}
                                    onChange={() =>
                                      handleTogglePermissionRow(
                                        r.employeeId,
                                        "pdfReports",
                                        !permissions.pdfReports
                                      )
                                    }
                                  />
                                </PermissionGridItem>
                                <PermissionGridItem>
                                  <span>Special permission</span>
                                  <PermissionCheckboxInput
                                    checked={permissions.specialPermission}
                                    disabled={permissionsDisabled}
                                    onChange={() =>
                                      handleTogglePermissionRow(
                                        r.employeeId,
                                        "specialPermission",
                                        !permissions.specialPermission
                                      )
                                    }
                                  />
                                </PermissionGridItem>
                                <PermissionGridItem>
                                  <span>Access analytics</span>
                                  <PermissionCheckboxInput
                                    checked={permissions.accessAnalytics}
                                    disabled={permissionsDisabled}
                                    onChange={() =>
                                      handleTogglePermissionRow(
                                        r.employeeId,
                                        "accessAnalytics",
                                        !permissions.accessAnalytics
                                      )
                                    }
                                  />
                                </PermissionGridItem>
                                <PermissionGridItem>
                                  <span>Nora AI</span>
                                  <PermissionCheckboxInput
                                    checked={permissions.noraAI}
                                    disabled={permissionsDisabled}
                                    onChange={() =>
                                      handleTogglePermissionRow(
                                        r.employeeId,
                                        "noraAI",
                                        !permissions.noraAI
                                      )
                                    }
                                  />
                                </PermissionGridItem>
                              </PermissionGrid>
                              {permissions.accessAnalytics ? (
                                <AnalyticsGrid>
                                  {ANALYTICS_CARD_PERMISSIONS.map((card) => (
                                    <AnalyticsGridItem key={card.key}>
                                      <div>
                                        <AnalyticsTitle>{card.label}</AnalyticsTitle>
                                        <AnalyticsCopy>{card.description}</AnalyticsCopy>
                                      </div>
                                      <PermissionCheckboxInput
                                        checked={analyticsPermissions?.[card.key]}
                                        disabled={permissionsDisabled}
                                        onChange={() =>
                                          handleToggleAnalyticsCard(
                                            r.employeeId,
                                            card.key,
                                            !analyticsPermissions?.[card.key]
                                          )
                                        }
                                      />
                                    </AnalyticsGridItem>
                                  ))}
                                </AnalyticsGrid>
                              ) : (
                                <ExpandedHint>Enable Access analytics to choose dashboards.</ExpandedHint>
                              )}
                            </ExpandedSection>
                          </ExpandedTd>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </Table>
            <Actions>
              <SecondaryButton onClick={() => setStep(1)}>Back</SecondaryButton>
              <PrimaryButton onClick={() => { resetState(); onBack && onBack(); }}>Done</PrimaryButton>
            </Actions>
          </>
        )}
        {confirmDialog.open && (
          <ConfirmOverlay>
            <ConfirmCard>
              <ConfirmTitle>Confirm Action</ConfirmTitle>
              <ConfirmMessage>{confirmDialog.message}</ConfirmMessage>
              <ConfirmActions>
                <SecondaryButton
                  onClick={() => {
                    const fn = confirmDialog.onCancel;
                    setConfirmDialog({
                      open: false,
                      type: null,
                      manager: null,
                      message: '',
                      onConfirm: null,
                      onCancel: null,
                    });
                    fn && fn();
                  }}
                >
                  Cancel
                </SecondaryButton>
                <PrimaryButton
                  onClick={() => {
                    const fn = confirmDialog.onConfirm;
                    setConfirmDialog({
                      open: false,
                      type: null,
                      manager: null,
                      message: '',
                      onConfirm: null,
                      onCancel: null,
                    });
                    fn && fn();
                  }}
                >
                  Confirm
                </PrimaryButton>
              </ConfirmActions>
            </ConfirmCard>
          </ConfirmOverlay>
        )}
      </Content>
    </PageContainer>
  );
};

// Page styles (aligned with AccessV2 aesthetic)
const PageContainer = styled.div`
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  margin-bottom: 24px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;

const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Title = styled.h1`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 28px;
  color: #414141;
  margin: 0 0 4px 0;
`;

const Subtitle = styled.p`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 14px;
  color: #7e7e7e;
  margin: 0;
`;

const Content = styled.div``;

const Instructions = styled.div`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 14px;
  color: #414141;
  margin-bottom: 12px;
`;

const SmallHint = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 12px;
  color: #7e7e7e;
  margin-top: 6px;
`;

const Textarea = styled.textarea`
  width: 100%;
  min-height: 160px;
  padding: 14px;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  font-family: "Poppins";
  font-size: 14px;
  resize: vertical;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #476dfa;
    box-shadow: 0 0 0 3px rgba(71, 109, 250, 0.1);
  }
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 16px;
`;

// Confirmation dialog styles
const ConfirmOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ConfirmCard = styled.div`
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 480px;
  padding: 20px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.15);
`;

const ConfirmTitle = styled.h4`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 16px;
  color: #414141;
  margin: 0 0 8px 0;
`;

const ConfirmMessage = styled.p`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 14px;
  color: #7e7e7e;
  margin: 0 0 16px 0;
`;

const ConfirmActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const PrimaryButton = styled.button`
  padding: 12px 20px;
  background: #476dfa;
  border: none;
  border-radius: 8px;
  color: white;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover:not(:disabled) {
    background: #3d5ce6;
  }
  &:disabled {
    background: #cccccc;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled.button`
  padding: 12px 20px;
  background: #f8f9fa;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  color: #414141;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover {
    background: #e9ecef;
    border-color: #476dfa;
  }
`;

const CancelButton = styled(SecondaryButton)``;

const StepHeader = styled.h3`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 16px;
  color: #414141;
  margin: 0 0 12px 0;
`;

const StepHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
`;

const Th = styled.th`
  text-align: left;
  padding: 12px;
  background: #f8f9fa;
  border-bottom: 1px solid #e0e0e0;
  font-family: "Poppins";
  font-weight: 600;
  font-size: 13px;
  color: #414141;
`;

const Td = styled.td`
  padding: 12px;
  border-bottom: 1px solid #f0f0f0;
  font-family: "Poppins";
  font-size: 13px;
  color: #414141;
`;

const RowActions = styled.div`
  display: flex;
  gap: 8px;
`;

const SavingText = styled.span`
  font-family: "Poppins";
  font-size: 12px;
  color: #6b7280;
  align-self: center;
`;

const PreviewStatusRow = styled.div`
  margin: 6px 0 12px 0;
  font-family: "Poppins";
  font-size: 12px;
  color: #6b7280;
`;

const ProcessProgressWrap = styled.div`
  margin-top: 10px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 12px;
  background: white;
`;

const ProcessProgressLine = styled.div`
  font-family: "Poppins";
  font-size: 13px;
  color: #111827;
  font-weight: 500;
`;

const ProcessProgressSubline = styled.div`
  margin-top: 4px;
  font-family: "Poppins";
  font-size: 12px;
  color: #6b7280;
`;

const ProgressBar = styled.div`
  margin-top: 10px;
  height: 8px;
  border-radius: 999px;
  background: #eef2ff;
  border: 1px solid #e5e7eb;
  overflow: hidden;
`;

const ProgressBarFill = styled.div`
  height: 100%;
  background: #476dfa;
  transition: width 0.2s ease;
`;

const BlockingOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(17, 24, 39, 0.55);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const BlockingCard = styled.div`
  width: 100%;
  max-width: 520px;
  background: white;
  border-radius: 14px;
  border: 1px solid rgba(229, 231, 235, 1);
  padding: 18px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.18);
`;

const BlockingTitle = styled.div`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 16px;
  color: #111827;
`;

const BlockingSubtext = styled.div`
  margin-top: 6px;
  font-family: "Poppins";
  font-size: 12px;
  color: #6b7280;
`;

const BlockingBar = styled.div`
  margin-top: 12px;
  height: 10px;
  border-radius: 999px;
  background: #eef2ff;
  border: 1px solid #e5e7eb;
  overflow: hidden;
`;

const BlockingBarFill = styled.div`
  height: 100%;
  background: #476dfa;
  transition: width 0.2s ease;
`;

const BlockingHint = styled.div`
  margin-top: 10px;
  font-family: "Poppins";
  font-size: 12px;
  color: #6b7280;
`;

const BulkActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const PresetCard = styled.div`
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
`;

const PresetHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 12px;
`;

const PresetTitle = styled.div`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 14px;
  color: #414141;
`;

const PresetHint = styled.div`
  font-family: "Poppins";
  font-size: 12px;
  color: #6b7280;
  margin-top: 4px;
`;

const PresetActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const PresetGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 10px;
`;

const PresetItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  border: 1px solid #f1f1f1;
  border-radius: 8px;
  background: #fafafa;
`;

const PresetLabel = styled.div`
  font-family: "Poppins";
  font-size: 12px;
  color: #414141;
  font-weight: 500;
`;

const PresetSubtext = styled.div`
  font-family: "Poppins";
  font-size: 11px;
  color: #7e7e7e;
  margin-top: 2px;
`;

const PresetAnalyticsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 10px;
  margin-top: 12px;
`;

const PresetAnalyticsItem = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  align-items: center;
  padding: 10px 12px;
  border: 1px solid #f1f1f1;
  border-radius: 8px;
  background: #fbfbfc;
`;

const AuditCard = styled.div`
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
`;

const AuditHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 12px;
`;

const AuditTitle = styled.div`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 14px;
  color: #414141;
`;

const AuditHint = styled.div`
  font-family: "Poppins";
  font-size: 12px;
  color: #6b7280;
  margin-top: 4px;
  max-width: 720px;
`;

const AuditActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
`;

const AuditProgressRow = styled.div`
  margin: 6px 0 12px 0;
  font-family: "Poppins";
  font-size: 12px;
  color: #6b7280;
`;

const AuditError = styled.div`
  margin-top: 8px;
  font-family: "Poppins";
  font-size: 12px;
  color: #b91c1c;
`;

const AuditEmpty = styled.div`
  font-family: "Poppins";
  font-size: 12px;
  color: #6b7280;
  padding: 10px 0;
`;

const AuditTableWrap = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
`;

const PrimaryMini = styled.button`
  padding: 8px 12px;
  background: #476dfa;
  border: none;
  border-radius: 6px;
  color: white;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 12px;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover:not(:disabled) { background: #3d5ce6; }
  &:disabled { background: #cccccc; cursor: not-allowed; }
`;

const DangerMini = styled(PrimaryMini)`
  background: #ef4444;
  &:hover:not(:disabled) { background: #dc2626; }
`;

const SecondaryMini = styled.button`
  padding: 8px 12px;
  background: #f8f9fa;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  color: #414141;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #e9ecef;
    border-color: #476dfa;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const GhostMini = styled(SecondaryMini)`
  background: transparent;
  border-style: dashed;
`;

const PermissionTh = styled.th`
  text-align: center;
  padding: 12px;
  background: #f8f9fa;
  border-bottom: 1px solid #e0e0e0;
  font-family: "Poppins";
  font-weight: 600;
  font-size: 11px;
  color: #414141;
`;

const PermissionTd = styled.td`
  padding: 12px;
  border-bottom: 1px solid #f0f0f0;
  text-align: center;
`;

const AnchorsCell = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  max-width: 260px;
`;

const AnchorsInlineList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

const AnchorChip = styled.span`
  padding: 2px 6px;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  font-family: "Poppins";
  font-size: 11px;
  color: #374151;
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const AnchorsOverflowButton = styled.button`
  padding: 0;
  margin: 0;
  border: none;
  background: transparent;
  font-family: "Poppins";
  font-size: 11px;
  color: #476dfa;
  cursor: pointer;
  text-decoration: none;
  display: inline-flex;
  align-items: center;

  &:hover:not(:disabled) {
    text-decoration: underline;
  }

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }
`;

const AnchorsMeta = styled.div`
  font-family: "Poppins";
  font-size: 11px;
  color: #6b7280;
`;

const PermissionHeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;

  span {
    font-size: 11px;
  }
`;

const PermissionCheckboxInput = styled.input.attrs({ type: "checkbox" })`
  width: 16px;
  height: 16px;
  cursor: pointer;
`;

const ExpandedTd = styled.td`
  padding: 0;
  background: #f9fafb;
  border-top: 1px solid #e5e7eb;
`;

const ExpandedSection = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ExpandedAnchorsBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ExpandedAnchorsTitle = styled.div`
  font-family: "Poppins";
  font-size: 12px;
  font-weight: 500;
  color: #111827;
`;

const ExpandedAnchorsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  max-height: 140px;
  overflow-y: auto;
`;

const ExpandedHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ExpandedTitle = styled.div`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 13px;
  color: #414141;
`;

const ExpandedHint = styled.div`
  font-family: "Poppins";
  font-size: 12px;
  color: #6b7280;
`;

const PermissionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 10px;
`;

const PermissionGridItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
`;

const AnalyticsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 10px;
`;

const AnalyticsGridItem = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  align-items: center;
  padding: 10px 12px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
`;

const AnalyticsTitle = styled.div`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 12px;
  color: #1f2937;
`;

const AnalyticsCopy = styled.div`
  font-family: "Poppins";
  font-size: 11px;
  color: #6b7280;
  margin-top: 2px;
`;

export default BulkImportManagers;
